import DataStore from "../../stores/dataStore.js";
import DatasetService from "../datasetService.js";
import InternalEventBus from "../../utils/internalEventBus.js";
import InternalEvent from "../../../core/constants/internalEvent.js";
import { getServerSideFunction } from "../../../core/constants/serverSideFunctionList";
import UniqueIdGenerator from "../../utils/uniqueIdGenerator";

var derivedDataUpdater = require('./derived-data-updater.js');
var TYPES = require('../../../../src-client-lib/api/type-ids');
var GetDataByRecordLocationRequestType = require('../../../../src-client-lib/api/request/constants.js').GetDataByRecordLocationRequestType;

const timerWorker = new Worker('timer-worker.js');
const nonContinuousChartTypes = ['bubble', 'bar', 'bar2'];

function DataService() {
  // //////// private members
  var callbackIds = {};
  var subscriptions = {};
  var activeHistoricDataLoaders = {};

  let serverSideFunctionCache = {};
  let serverSideFunctionRequests = {};

  const requestTracker = {};
  let serverSideFunctionTracker = {};

  const intervalCallbacks = {};
  timerWorker.onmessage = (e) => {
    if (intervalCallbacks[e.data.id]) {
      intervalCallbacks[e.data.id](e.data.time);
    }
  };

  // store the latest data for checking stale data on resolution mode
  const latestDataCache = {};


  this.init = function () {
    InternalEventBus.subscribe(InternalEvent.Dashboard.DASHBOARD_VARIABLE_CHANGED, this.flushServerSideFunctionCache);
  };

  function listenRealtimeData(data) {
    if (data.typeId === TYPES.SubscribeRealTimeDataResponse) return;

    if (data.epochSecond == null) {
      data.epochSecond = parseInt(data.timestamp / 1000);
    }
    Object.freeze(data);
    DataStore.store(data.dataset, data);
    derivedDataUpdater.update(data.dataset);
    InternalEventBus.publish(data.dataset, data);
  }

  /***
   * Sends a real-time data subscription request to the server.
   * It doesn't send any real-time subscription requests if legends has any server-side function.
   *
   * @param legends
   */
  this.subscribe = function (legends) {
    let hasServerSideFunction = false;
    legends.forEach(l => {
      hasServerSideFunction = l.serverSideFunction && l.serverSideFunction.length > 0;
    });

    if (!hasServerSideFunction) {
      for (let i = 0; i < legends.length; i++) {
        for (let key in legends[i].mappings) {
          const datasetId = legends[i].mappings[key].datasetId;
          if (!subscriptions[datasetId]) {
            subscriptions[datasetId] = true;
            callbackIds[datasetId] = smashClient.registerRealtimeDataCallback(datasetId, listenRealtimeData);
          }
        }
      }
    }
  };

  this.unsubscribe = function (dataStreamIds) {
    if (dataStreamIds.length > 0) {
      for (var index in dataStreamIds) {
        var datasetId = dataStreamIds[index];

        if (subscriptions[datasetId]) {
          var callbackId = callbackIds[datasetId];
          delete subscriptions[datasetId];
          delete callbackIds[datasetId];
          smashClient.deregisterRealtimeDataCallback(callbackId);
          DataStore.remove(datasetId);
        }
        else {
          derivedDataUpdater.deregister(datasetId);
        }
      }
    }
  };

  this.cancelSnapshotDataLoad = function (registrationToken) {
    delete activeHistoricDataLoaders[registrationToken];
  };

  this.publishData = function (datasetId, dataProfile, data, callback) {
    smashClient.publishData(datasetId, dataProfile.name, dataProfile.createDatasetEventTypeId, data, callback);
  };

  /***
   * Requests the latest snapshot data.
   *
   * @param requestParams  array of request parmas { datasetId, fieldId }
   * @param noOfRecords
   * @returns {Promise}
   */
  this.getLatestData = function (requestParams, noOfRecords, options, previousUserAction) {
    if (options.resolution) {
      return this.getLatestResolutionData(requestParams, options)
    } else {
      if (previousUserAction) {
        const datasetIds = requestParams.datasets.map(r => r.datasetId);
        const requestDate = new Date(previousUserAction.value).toISOString();

        switch (previousUserAction.type) {
          case 'set_time': {
            return this.getSnapshotDataAfterAndBeforeTime(datasetIds, requestDate, noOfRecords);
          }
          case 'move_left': {
            return this.getSnapshotDataBeforeTime(datasetIds, requestDate, noOfRecords);
          }
          case 'move_right': {
            return this.getSnapshotDataAfterTime(datasetIds, requestDate, noOfRecords);
          }
        }

        // const requestDate = new Date(previousDataPoint).toISOString();
        // const datasetIds = requestParams.datasets.map(r => r.datasetId);
        // return this.getSnapshotDataBeforeTime(datasetIds, requestDate, noOfRecords)
      }
      return this.getLatestTickData(requestParams, noOfRecords)
    }
  };

  this.getLatestTickData = function (requestParams, noOfRecords) {
    let promises = requestParams.datasets.map(params => {
      return this.getLatestDataForSingleDataset({
        datasetId: params.datasetId,
        fieldId: params.fieldId,
        noOfRecords
      });
    });

    return Promise.all(promises)
      .then(result => {
        const datasetWithNoSubscriptions = result.filter(r => r.authorized === false).map(r => r.datasetId);
        if (result.length === 0) {
          return result;
        }
        // this.checkStaleDataForTickData(result, options.maxRepeatDuration);
        let consolidatedResult = this.consolidateSnapshotData(result);
        let trimmedResult = {};

        consolidatedResult.forEach((singleDatasetSnapshot, index) => {
          trimmedResult[result[index].datasetId] = singleDatasetSnapshot
        });

        let snapshotDataCount = consolidatedResult[0].length;
        let sliceBegin = snapshotDataCount - noOfRecords;
        if (sliceBegin < 0) {
          sliceBegin = 0;
        }
        let sliceEnd = snapshotDataCount;
        consolidatedResult.map((singleDatasetSnapshot, index) => {
          trimmedResult[result[index].datasetId] = singleDatasetSnapshot.slice(sliceBegin, sliceEnd);
        });

        return {
          data: trimmedResult,
          noSubscriptions: datasetWithNoSubscriptions
        };
      });
  };

  this.getLatestResolutionData = function (requestParams, resolutionOptions) {
    let { startTime, endTime, resolution } = resolutionOptions;
    const eventRequests = requestParams.events.map(e => {
      return this.getDataForTimeRange(e.datasetId, startTime.toISOString(), endTime.toISOString());
    });

    const latestDataRequests = requestParams.datasets.map(params => {
      return this.getLatestDataForSingleDataset({
        datasetId: params.datasetId,
        fieldId: params.fieldId,
        noOfRecords: 1
      });
    });

    const datasetRequests = requestParams.datasets.map(r => {
      return {
        name: 'TSCalculate',
        requestId: r.datasetId,
        params: [
          r.datasetId,
          r.fieldId,
          startTime.toISOString(),
          endTime.toISOString(),
          resolution[3],  // timeWindowQuantity
          resolution[2],  // timeWindowUnit
          nonContinuousChartTypes.indexOf(r.chartType) > -1 ? 3 : 7,  // functionType
          null, // repeatTimeQuantity
          null, // repeatTimeUnit
          true, // includeRealtimeUpdate
          null, // businessDayOnly
          true  // emitEmptyWindow
        ]
      }
    });

    return Promise.all([
      Promise.all(eventRequests),
      this.getServerSideFunctionResult(datasetRequests),
      Promise.all(latestDataRequests)
    ]).then(result => {
      this.storeLatestData(result[2]);
      Object.keys(result[1].result.data).forEach(key => {
        this.checkStaleDataForResolutionData({
          datasetId: key,
          data: Object.values(result[1].result.data[key])
        }, resolutionOptions);
      });
      const data = result[1].result.data;
      Object.keys(data).forEach(datasetId => {
        const datasetData = data[datasetId];
        const latestData = datasetData[datasetData.length - 1];
        DataStore.store(datasetId, latestData);
      });
      return {
        data,
        eventData: result[0]
      };
    });
  };

  this.storeLatestData = function (latestDataResponses) {
    latestDataResponses.forEach(r => {
      latestDataCache[r.datasetId] = r.data[0];
    });
  };

  /**
   * Check stale data on resolution mode.
   * @param resolutionDataResult
   * @param latestDataResult
   * @param resolutionOptions
   */
  this.checkStaleDataForResolutionData = function ({ datasetId, data }, resolutionOptions) {
    let { resolution, maxRepeatDuration } = resolutionOptions;
    const resolutionInMillis = resolution[1] * 1000;
    if (maxRepeatDuration > 0) {

      const latestData = latestDataCache[datasetId];
      if (latestData) {
        const maxTimestamp = latestData.timestamp + maxRepeatDuration * 1000 * 60;
        const maxTimeBucket = Math.floor(maxTimestamp / resolutionInMillis) * resolutionInMillis;

        // any data with timestamp grater than maxTimestamp are considered stale data
        data.forEach(d => {
          if (d.timestamp > maxTimeBucket) {
            d.isStale = true;
          }
        });
      }
    }
  };

  /***
   * Requests the latest snapshot data for a single dataset.
   *
   * @param datasetId
   * @param fieldId
   * @param noOfRecords
   * @param interval          update interval in second
   * @returns promise<any>    {datasetId:string, data: any[], authorized: boolean}
   *                          if authorized is false, user has no subscriptions
   */
  this.getLatestDataForSingleDataset = function ({ datasetId, fieldId, noOfRecords, interval }) {
    if (interval > 0) {
      return this.getLatestDataForIntervalUpdate(datasetId, fieldId, noOfRecords, interval);
    } else {
      return this.getLatestDataForRealtimeUpdate(datasetId, noOfRecords);
    }
  };

  /**
   * For the latest data request with time interval, we need to use TSCalculate with emitEmptyWindow option.
   * */
  this.getLatestDataForIntervalUpdate = function (datasetId, fieldId, noOfRecords, interval) {
    const now = new Date().getTime();
    const endTime = parseInt(now / (interval * 1000)) * interval * 1000;
    const startTime = endTime - interval * 1000 * noOfRecords;
    const params = [datasetId, fieldId, new Date(startTime).toISOString(),
      new Date(endTime).toISOString(), interval, 0, 3, null, null, false, false, true];

    return this.addServerSideFunctionToCache({
      requestId: datasetId,
      name: 'TSCalculate',
      params
    }).then(result => {
      // requested data has to be in data[fieldName]
      result.data.forEach(d => d[fieldId] = d.value);
      return result;
    });
  };


  this.getLatestDataForRealtimeUpdate = function (datasetId, noOfRecords) {
    return new Promise((resolve, reject) => {
      const data = [];
      smashClient.getLatestData(datasetId, noOfRecords, (response) => {
        if (response.typeId === TYPES.AuthorizationErrorResponse) {
          resolve({ datasetId, data, authorized: false });
        } else if (response.typeId !== TYPES.GetLatestDataResponse) {
          if (response.lastResponse) {
            if (response.responseIndex > -1) {
              data.push(response);
            }

            // store the latest data to data store
            if (data.length) {
              const lastData = data[data.length - 1];
              DataStore.store(datasetId, lastData);
            }

            this.trackRequest(datasetId, true);

            resolve({ datasetId, data, authorized: true });
          } else {
            data.push(response);
          }
        }
      });
    });
  };

  this.getLatestDataForDelayedUpdate = function (datasetId, noOfRecords, dataAccessAge) {
    return new Promise((resolve, reject) => {
      const data = [];
      const now = new Date().getTime();
      // subtract dataAccessAge from current time
      const targetTime = now - dataAccessAge * 1000 - 10000;
      const afterTimeString = new Date(targetTime).toISOString().slice(0, -1);
      this.getDataAfterTime(datasetId, afterTimeString, 1)
        .then(snapshotResult => {
          if (snapshotResult.data.length === 1) {
            const eventId = snapshotResult.data[0].eventId.replace(/-/g, "");
            const fileBlockIndex = parseInt("0x" + eventId.substr(0, 16));
            const fileBlockRecordIndex = parseInt("0x" + eventId.substr(16, 32));
            this.getDataByRecordLocation(datasetId, fileBlockIndex, fileBlockRecordIndex, 1, noOfRecords)
              .then(result => {
                this.trackRequest(datasetId, true);
                resolve(result);
              });
          }
        });
    });
  };


  /***
   * Requests snapshot data using GetDataByRecordLocation.
   *
   * @param requests        [{ datasetId, fileBlockIndex, fileBlockRecordIndex, isMyDatasetUpdate }, ...]
   * @param noOfRecords     -1 for all
   * @param trimSide
   * @returns {Promise}     { dataset1: [data], dataset2: [data], ... } or null
   */
  this.getSnapshotData = function (requests, noOfRecords, trimSide) {
    const promises = requests.map(request => {
      const requestType = getDataByRecordLocationRequestType(request, trimSide);
      return this.getDataByRecordLocation(request.datasetId, request.fileBlockIndex,
        request.fileBlockRecordIndex, requestType, noOfRecords)
        .then(result => {
          result.request = request;
          return result;
        });
    });

    return Promise.all(promises)
      .then(results => {
        let isEmpty = true;
        results.forEach(result => {
          if (result.data.length !== 0) {
            isEmpty = false;
          }
        });

        if (isEmpty) {
          return null;
        }

        const consolidatedResult = this.consolidateSnapshotData(results);
        const trimmedResult = {};
        const snapshotDataCount = consolidatedResult[0].length;
        let sliceBegin = trimSide === "left" ? snapshotDataCount - noOfRecords : 0;
        if (sliceBegin < 0) {
          sliceBegin = 0;
        }
        const sliceEnd = trimSide === "left" ? snapshotDataCount : noOfRecords;
        consolidatedResult.forEach((singleDatasetSnapshot, index) => {
          let identifier = results[index].datasetId;
          if (results[index].request.isFunctionRequest) {
            identifier = results[index].request.requestId;
          }
          trimmedResult[identifier] = singleDatasetSnapshot.slice(sliceBegin, sliceEnd);
        });
        return trimmedResult;
      });
  };


  /**
   * case: Left side
   * BACKWARD_EXCLUSIVE  <==  [1, 2, 3, 4, 5]
   * BACKWARD_INCLUSIVE  <==  [3(filled), 5, 6, 7, 8]
   *  3 is filled data from previous data point so actual data point hasn't been rendered
   *
   *
   * case: Right side
   * [1, 2, 3, 4, 5]           ===> FORWARD_EXCLUSIVE
   * [3, 4, 2, 4, 4(filled)]   ===> FORWARD_EXCLUSIVE
   *
   * @param {*} request
   * @param {*} trimSide
   */
  function getDataByRecordLocationRequestType(request, trimSide) {
    if (trimSide === "left") {
      return request.isMyDatasetUpdate ? GetDataByRecordLocationRequestType.BACKWARD_EXCLUSIVE :
        GetDataByRecordLocationRequestType.BACKWARD_INCLUSIVE;
    } else {
      return GetDataByRecordLocationRequestType.FORWARD_EXCLUSIVE;
    }
  }

  this.getIntervalData = function (requests, noOfRecords, trimSide, updateInterval) {
    const promises = requests.map(request => {
      return this.getIntervalDataForSingleDataset(request.datasetId, request.fieldId,
        request.timestamp, noOfRecords, trimSide, updateInterval);
    });

    return Promise.all(promises)
      .then(result => {
        let consolidatedResult = this.consolidateSnapshotData(result);
        let trimmedResult = {};
        let snapshotDataCount = consolidatedResult[0].length;
        let sliceBegin = snapshotDataCount - noOfRecords;
        if (sliceBegin < 0) {
          sliceBegin = 0;
        }
        let sliceEnd = snapshotDataCount;
        consolidatedResult.forEach((singleDatasetSnapshot, index) => {
          trimmedResult[result[index].datasetId] = singleDatasetSnapshot.slice(sliceBegin, sliceEnd);
        });
        return trimmedResult;
      });
  };

  this.getIntervalDataForSingleDataset = function (datasetId, fieldId, timestamp, numDataPoints, trimSide, updateInterval) {
    return new Promise((resolve, reject) => {
      const result = {
        datasetId: datasetId,
        data: []
      };
      let startTime, endTime;
      const duration = updateInterval * 1000 * numDataPoints;
      if (trimSide === "left") {
        endTime = timestamp;
        startTime = endTime - duration;
      } else {
        startTime = timestamp;
        endTime = startTime + duration;
      }

      const functionParams = [datasetId, fieldId, new Date(startTime).toISOString(),
        new Date(endTime).toISOString(), updateInterval, 0, 3];
      smashClient.serverSideFunctionRequest(
        datasetId,
        res => {
          res[fieldId] = res.value;
          result.data.push(res);
          if (res.lastResponse) {
            resolve(result);
          }
        },
        "TSCalculate",
        functionParams);
    });
  };

  /***
   * Gets snapshot data for a single dataset using the record location.
   * It will resolve promise when it gets BatchJobDoneResponse from the server.
   *
   * @param datasetId
   * @param fileBlockIndex
   * @param fileBlockRecordIndex
   * @param requestType
   * @param noOfRecords
   * @returns {Promise} { datasetId, data }
   */
  this.getDataByRecordLocation = function (datasetId, fileBlockIndex, fileBlockRecordIndex,
    requestType, noOfRecords) {
    const group = `getDataByRecordLocation ${datasetId} ${fileBlockIndex} ${fileBlockRecordIndex} ${noOfRecords}`;

    return new Promise((resolve, reject) => {
      let result = {
        datasetId: datasetId,
        data: []
      };
      smashClient.getDataByRecordLocation(datasetId, fileBlockIndex, fileBlockRecordIndex,
        requestType, noOfRecords, (response) => {
          if (response.typeId !== TYPES.GetDataByRecordLocationResponse) {
            if (response.lastResponse) {
              if (response.responseIndex > -1) {
                result.data.push(response);
              }
              let lastData = result.data[result.data.length - 1];
              DataStore.store(result.datasetId, lastData);
              console.group(group);
              console.log(result);
              console.groupEnd();
              resolve(result);
            } else {
              result.data.push(response);
            }
          }
        });
    });
  };

  /***
   * Requests snapshot data after specific datetime.
   *
   * @param datasets
   * @param snapshotStartTime
   * @param noOfRecords
   * @returns {Promise} { dataset1: [data], dataset2: [data], ... }
   */
  this.getSnapshotDataAfterTime = function (datasets, snapshotStartTime, noOfRecords) {
    let promises = datasets.map(datasetId => {
      return this.getDataAfterTime(datasetId, snapshotStartTime, noOfRecords);
    });

    return Promise.all(promises)
      .then(result => {
        let consolidatedResult = this.consolidateSnapshotData(result);
        let trimmedResult = {};
        consolidatedResult.map((singleDatasetSnapshot, index) => {
          trimmedResult[result[index].datasetId] = singleDatasetSnapshot.slice(0, noOfRecords);
        });
        return trimmedResult;
      });
  };

  this.getSnapshotDataBeforeTime = function (datasets, snapshotStartTime, noOfRecords) {
    let promises = datasets.map(datasetId => {
      return this.getDataBeforeTime(datasetId, snapshotStartTime, noOfRecords);
    });

    return Promise.all(promises)
      .then(result => {
        let consolidatedResult = this.consolidateSnapshotData(result);
        let trimmedResult = {};
        consolidatedResult.map((singleDatasetSnapshot, index) => {
          trimmedResult[result[index].datasetId] = singleDatasetSnapshot.slice(0, noOfRecords);
        });
        return trimmedResult;
      });
  };

  /***
   * Gets snapshot data for a single dataset using snapshot start time.
   * It will resolve promise when it gets BatchJobDoneResponse from the server.
   *
   * @param datasetId
   * @param snapshotStartTime
   * @param noOfRecords
   * @returns {Promise}
   */
  this.getDataAfterTime = function (datasetId, snapshotStartTime, noOfRecords) {
    return new Promise((resolve, reject) => {
      let result = {
        datasetId: datasetId,
        data: []
      };
      smashClient.getDataAfterTime(datasetId, snapshotStartTime, noOfRecords, response => {
        if (response.typeId !== TYPES.GetDataAfterTimeResponse) {
          if (response.lastResponse) {
            if (response.responseIndex > -1) {
              result.data.push(response);
            }
            let lastData = result.data[result.data.length - 1];
            // DataStore.store(result.datasetId, lastData);
            resolve(result);
          } else {
            result.data.push(response);
          }
        }
      });
    });
  };

  this.getDataBeforeTime = function (datasetId, snapshotEndTime, noOfRecords) {
    return new Promise((resolve, reject) => {
      this.getDataAfterTime(datasetId, snapshotEndTime, 1)
        .then(snapshotResult => {
          if (snapshotResult.data.length === 1) {
            const eventId = snapshotResult.data[0].eventId.replace(/-/g, "");
            const fileBlockIndex = parseInt("0x" + eventId.substr(0, 16));
            const fileBlockRecordIndex = parseInt("0x" + eventId.substr(16, 32));
            this.getDataByRecordLocation(datasetId, fileBlockIndex, fileBlockRecordIndex, 1, noOfRecords)
              .then(result => {
                resolve(result);
              });
          }
        });
    });
  };

  this.getSnapshotDataAfterAndBeforeTime = function (datasets, snapshotStartTime, noOfRecords) {
    let _this = this;
    const halfNumDataPoints = parseInt(noOfRecords / 2);
    let snapshotRight;

    return loadRightSide(datasets, snapshotStartTime, halfNumDataPoints)
      .then(data => {
        snapshotRight = data.snapshotResultForRightSide;
        return loadLeftSide(data.subsequentRequestForLeftSide, halfNumDataPoints);
      })
      .then(snapshotLeft => {
        let numberOfData;
        for (let datasetId in snapshotLeft) {
          numberOfData = snapshotLeft[datasetId].length + snapshotRight[datasetId].length;
          snapshotLeft[datasetId] =
            snapshotLeft[datasetId].concat(snapshotRight[datasetId]);

          // filling data
          let lastKnownData = null;
          for (let i = 0; i < snapshotLeft[datasetId].length; i++) {
            let data = snapshotLeft[datasetId][i];
            if (data == null) {
              if (lastKnownData) {
                snapshotLeft[datasetId][i] = lastKnownData;
              }
            } else {
              lastKnownData = Object.assign({}, data);
              lastKnownData.isMyDatasetUpdate = false;
            }
          }
        }
        return {
          data: snapshotLeft,
          numberOfData: numberOfData
        };
      });

    function loadRightSide(datasets, snapshotStartTime, noOfRecords) {
      let promises = datasets.map(datasetId => {
        return _this.getDataAfterTime(datasetId, snapshotStartTime, noOfRecords);
      });

      return Promise.all(promises)
        .then(snapshotResult => {
          let subsequentRequestForLeftSide = [];
          snapshotResult.forEach(singleDatasetSnapshot => {
            if (singleDatasetSnapshot.data.length > 0) {
              let eventId = singleDatasetSnapshot.data[0].eventId.replace(/-/g, "");
              subsequentRequestForLeftSide.push({
                datasetId: singleDatasetSnapshot.datasetId,
                fileBlockIndex: parseInt("0x" + eventId.substr(0, 16)),
                fileBlockRecordIndex: parseInt("0x" + eventId.substr(16, 32)),
                isMyDatasetUpdate: true
              });
            } else {
              subsequentRequestForLeftSide.push({
                datasetId: singleDatasetSnapshot.datasetId,
                isEmpty: true
              });
            }
          });
          let consolidatedResult = _this.consolidateSnapshotData(snapshotResult);
          let trimmedResult = {};
          consolidatedResult.map((singleDatasetSnapshot, index) => {
            trimmedResult[snapshotResult[index].datasetId] = singleDatasetSnapshot.slice(0, noOfRecords);
          });

          return {
            subsequentRequestForLeftSide: subsequentRequestForLeftSide,
            snapshotResultForRightSide: trimmedResult
          }
        });
    }

    function loadLeftSide(requests, noOfRecords) {
      const promises = requests.map(request => {
        const requestType = getDataByRecordLocationRequestType(request, "left");
        if (request.isEmpty) {
          return _this.getLatestDataForSingleDataset({ datasetId: request.datasetId, noOfRecords });
        } else {
          return _this.getDataByRecordLocation(request.datasetId, request.fileBlockIndex,
            request.fileBlockRecordIndex, requestType, noOfRecords);
        }
      });

      return Promise.all(promises)
        .then(snapshotResult => {
          const consolidatedResult = _this.consolidateSnapshotData(snapshotResult);
          const trimmedResult = {};
          const snapshotDataCount = consolidatedResult[0].length;
          let sliceBegin = snapshotDataCount - noOfRecords;
          if (sliceBegin < 0) {
            sliceBegin = 0;
          }
          const sliceEnd = snapshotDataCount;
          consolidatedResult.map((singleDatasetSnapshot, index) => {
            trimmedResult[snapshotResult[index].datasetId] = singleDatasetSnapshot.slice(sliceBegin, sliceEnd);
          });
          return trimmedResult;
        });
    }
  };


  this.getDataForTimeRange = function (datasetId, snapshotStartTime, snapshotEndTime) {
    const data = [];
    return new Promise((resolve, reject) => {
      smashClient.getDataForTimeRange(datasetId, snapshotStartTime, snapshotEndTime, -1, response => {
        data.push(response);
        if (response.lastResponse) {
          resolve(data);
        }
      });
    });
  };


  /***
   * consolidateSnapshotData example
   *
   * snapshotResult
   * dataset1 [1, 3, 5, 6, 10]
   * dataset2 [2, 3, 4, 5,  9]
   *
   * concatenatedData [ 1, 2, 3, 3, 4, 5, 5, 6, 9, 10]
   *
   * concatenatedDataByDataset [
   *     dataset 1 [ 1, null, 3, null, null, 5, null, 6, null, 10]
   *     dataset 2 [ null, 2, null, 3, 4, null, 5, null, 9, null]
   * ]
   *
   * filling null data with the last known value [
   *     dataset 1 [ 1, 1, 3, 3, 3, 5, 5, 6, 6, 10]
   *     dataset 2 [ null, 2, 2, 3, 4, 4, 5, 5, 9, 9]
   * ]
   *
   * @param snapshotResult
   * @returns {Array}
   */
  this.consolidateSnapshotData = function (snapshotResult) {
    let concatenatedData = [];
    let concatenatedDataByDataset = [];
    snapshotResult.forEach(singleDatasetSnapshotResult => {
      concatenatedData = concatenatedData.concat(singleDatasetSnapshotResult.data);
    });
    concatenatedData = concatenatedData.sort((a, b) => {
      return a.timestamp - b.timestamp
    });

    snapshotResult.forEach((snapshotResult, datasetIndex) => {
      const isServerSideFunctionResult = snapshotResult.serverSideFunctionResult;
      const datasetId = snapshotResult.datasetId;
      const requestId = snapshotResult.requestId;
      concatenatedDataByDataset[datasetIndex] = [];
      concatenatedData.forEach((data, dataIndex) => {
        // set original timestamp for stale data check
        data.originalTimestamp = data.timestamp;

        // for function request compare requestId and clientRefId
        if (data.dataset === datasetId || data.clientRefId === requestId) {
          // isMyDatasetUpdate flag indicates the data came from the server, not being filled manually.
          data.isMyDatasetUpdate = true;
          concatenatedDataByDataset[datasetIndex][dataIndex] = data;
        } else {
          concatenatedDataByDataset[datasetIndex][dataIndex] = null;
        }
      });
    });

    let filledSnapshotData = concatenatedDataByDataset.map((singleSnapshotData, datasetIndex) => {
      let lastKnownData = null;
      return singleSnapshotData.map((data, dataIndex) => {
        if (data === null) {
          if (lastKnownData != null) {
            // make sure filled data is not from the server.
            lastKnownData.isMyDatasetUpdate = false;
            // filled data should have null timestamp
            lastKnownData.timestamp = null;
            return Object.assign({}, lastKnownData);
          } else {
            return null;
          }
        } else {
          let timestamp = concatenatedDataByDataset[datasetIndex][dataIndex].timestamp;
          concatenatedDataByDataset[datasetIndex][dataIndex].epochSecond = parseInt(timestamp / 1000);
          lastKnownData = Object.assign({}, data);
          return data;
        }
      });
    });

    // all snapshot data at the specific column must have the same timestamp
    this.updateTimestamp(filledSnapshotData);

    return filledSnapshotData;
  };

  /**
   * Consolidating server-side function data removes duplicated timestamps.
   * @returns {{}}
   */
  this.consolidateServerSideFunctionData = function (serverSideFunctionResults, removeDuplicates = true) {
    // store timestamp from every data
    let timestamps = [];
    serverSideFunctionResults.forEach(result => {
      timestamps = timestamps.concat(result.data.map(d => d.timestamp));
    });

    timestamps = timestamps.sort((a, b) => a - b);

    if (removeDuplicates) {
      timestamps = Array.from(new Set(timestamps));
    }

    let consolidatedResult = {};

    const timestampsMap = {};
    timestamps.forEach((timestamp, index) => {
      if (timestampsMap[timestamp] === undefined) {
        timestampsMap[timestamp] = index;
      }
    });

    serverSideFunctionResults.forEach(result => {
      const { data: resultData, datasetId } = result;

      if (!consolidatedResult[datasetId]) {
        consolidatedResult[datasetId] = new Array(timestamps.length).fill(null);
      }

      // assign data to the result
      for (let i = 0; i < resultData.length; i++) {
        const data = resultData[i];
        const timestampIndex = timestampsMap[data.timestamp];
        const existingData = consolidatedResult[datasetId][timestampIndex];
        consolidatedResult[datasetId][timestampIndex] = Object.assign({}, data, existingData);
      }
    });

    // Filling data must be run separately from assigning data
    serverSideFunctionResults.forEach(result => {
      const { datasetId } = result;

      for (let i = 1; i < timestamps.length; i++) {
        if (!consolidatedResult[datasetId][i]) {
          const previousData = consolidatedResult[datasetId][i - 1];
          if (previousData) {
            consolidatedResult[datasetId][i] = Object.assign({}, previousData);
          }
        }
      }
    });

    return consolidatedResult;
  };


  this.getServerSideFunctionResult = function (requests, options = {}, noOfRecords, removeDuplicates = true) {
    let promises = requests.map(r => {
      return this.getSingleServerSideFunctionResult(r);
    });

    return Promise.all(promises)
      .then(result => {
        if (result.length === 0) {
          return { result };
        }
        if (options.maxRepeatDuration) {
          result.forEach(r => this.checkStaleDataForResolutionData({ datasetId: r.datasetId, data: r.data }, options));
        }
        const processedResult = this.consolidateServerSideFunctionData(result.filter(r => !r.isEventData), removeDuplicates);
        return {
          result: {
            data: processedResult,
            eventData: result.filter(r => r.isEventData).map(d => d.data)
          },
          noSubscriptions: result.filter(r => r.authorized === false).map(r => r.datasetId)
        }
      });
  };

  this.getSingleServerSideFunctionResult = function (request) {
    return this.addServerSideFunctionToCache(request);
  };

  this.updateTimestamp = function (snapshotData) {
    if (snapshotData.length > 0) {
      for (let i = 0; i < snapshotData[0].length; i++) {
        let maxTimestamp = 0, maxEpochSecond = 0;
        for (let j = 0; j < snapshotData.length; j++) {
          if (snapshotData[j][i] !== null) {
            let timestamp = snapshotData[j][i].timestamp;
            if (timestamp != null && timestamp > maxTimestamp) {
              maxTimestamp = timestamp;
              maxEpochSecond = snapshotData[j][i].epochSecond;
            }
          }
        }
        for (let j = 0; j < snapshotData.length; j++) {
          if (snapshotData[j][i] !== null) {
            snapshotData[j][i].timestamp = maxTimestamp;
            snapshotData[j][i].epochSecond = maxEpochSecond;
          }
        }
      }
    }
  };

  this.addServerSideFunctionToCache = function (request) {
    const { name, trackingId, requestId } = request;
    const paramStr = request.params.join(",");

    if (!serverSideFunctionCache.hasOwnProperty(name)) {
      serverSideFunctionCache[name] = [];
    }

    const fnCache = serverSideFunctionCache[name].find(fn => fn.paramStr === paramStr);

    return new Promise((resolve, reject) => {
      if (fnCache) {
        if (fnCache.status === 'pending') {
          // this sever side function request already sent to the server. Therefore, just adding the resolve function.
          // the resolve function will be invoked once the server side function completed.
          fnCache.resolvers.push(
            { trackingId, requestId, resolver: resolve });
        } else {
          // this server side function result already in the cache.
          const resultCopy = JSON.parse(JSON.stringify(fnCache.result));
          // server side function request id and clientRefId of the response must be same.
          resultCopy.data.forEach(d => d.clientRefId = requestId);
          resultCopy.datasetId = requestId;
          resolve(resultCopy);
        }
      } else {
        serverSideFunctionCache[name].push({
          function: name,
          paramStr,
          result: null,
          resolvers: [{ trackingId, requestId, resolver: resolve }],
          status: 'pending'
        });

        this.sendServerSideFunctionRequest(request);
      }
    });
  };

  this.sendServerSideFunctionRequest = function (request) {
    const trackingId = UniqueIdGenerator.uuid();
    request.trackingId = trackingId;
    const result = {
      trackingId,
      name: request.name,
      datasetId: request.requestId,
      requestId: request.requestId,
      isEventData: request.isEventData,
      data: [],
      serverSideFunctionResult: true,
      authorized: true,
    };

    serverSideFunctionRequests[request.trackingId] = request;
    smashClient.serverSideFunctionRequest(
      request.trackingId,
      res => this._serverSideFunctionResponseCallback(request, result, res),
      request.name,
      request.params);
    // this.trackServerSideRequest(request);
  };

  this._serverSideFunctionResponseCallback = function (request, result, response) {
    const fn = getServerSideFunction(request.name);
    if (fn.handler) {
      fn.handler(request, response);
    }

    // skip confirmation message
    if (fn.confirmResponseTypeId && response.typeId === fn.confirmResponseTypeId) {
      return;
    }

    if (response.typeId === TYPES.AuthorizationErrorResponse) {
      result.authorized = false;
      this._completeServerSideFunction(result, response.clientRefId);
      return;
    }

    response.isMyDatasetUpdate = true;
    response.epochSecond = parseInt(response.timestamp / 1000);

    if (response.lastResponse) {
      // responseIndex === -1 means no result
      if (response.responseIndex > -1) {
        result.data.push(response);
      }

      if (fn.afterDataLoaded) {
        result.data = fn.afterDataLoaded(request.params, result.data);
      }

      this._completeServerSideFunction(result, request.trackingId);
    } else {
      result.data.push(response);
    }
  };

  this._completeServerSideFunction = function (result, trackingId) {
    const request = serverSideFunctionRequests[trackingId];
    const fnName = request.name;
    const paramStr = request.params.join(",");
    const fnCache = serverSideFunctionCache[fnName].find(fn => fn.paramStr === paramStr);
    fnCache.status = 'completed';
    fnCache.result = result;
    fnCache.resolvers.map(r => {
      const resultCopy = JSON.parse(JSON.stringify(result));
      resultCopy.data.forEach(d => d.clientRefId = r.requestId);
      resultCopy.datasetId = r.requestId;
      resultCopy.parameters = request.params;
      r.resolver(resultCopy);
    });
    fnCache.resolvers = [];
    // this.trackServerSideRequest(clientRefId, true, result);
  };

  this.flushServerSideFunctionCache = function () {
    console.log('flushServerSideFunctionCache');
    for (let prop in serverSideFunctionCache) {
      serverSideFunctionCache[prop].forEach(c => {
        if (c.status !== 'completed') {
          console.log(c);
        }
      })
    }
    serverSideFunctionCache = {};
    serverSideFunctionRequests = {};
    serverSideFunctionTracker = {};
  };

  this.trackRequest = function (dataset, received) {
    if (received) {
      requestTracker[dataset]--;
    } else {
      if (requestTracker.hasOwnProperty(dataset)) {
        requestTracker[dataset]++
      } else {
        requestTracker[dataset] = 1;
      }
    }
    // console.log(requestTracker);
  };

  this.trackServerSideRequest = function (data, received, result) {
    if (received) {
      serverSideFunctionTracker[data].pendingRequest--;
      serverSideFunctionTracker[data].result = result;
    } else {
      if (requestTracker.hasOwnProperty(data.requestId)) {
        serverSideFunctionTracker[data.requestId].pendingRequest++;
      } else {
        serverSideFunctionTracker[data.requestId] = data;
        serverSideFunctionTracker[data.requestId].pendingRequest = 1;
      }
    }
    console.log(serverSideFunctionTracker);
  };

  this.registerInterval = function (id, delay, callback) {
    intervalCallbacks[id] = callback;
    timerWorker.postMessage({
      method: 'registerInterval',
      args: [id, delay]
    });
  };

  this.deregisterInterval = function (id) {
    timerWorker.postMessage({
      method: 'deregisterInterval',
      args: [id]
    });
  };

  this.init();
}

module.exports = new DataService();





// WEBPACK FOOTER //
// ./src/core/services/data-service/data-service.js